import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ReportResultsTable } from "@/components/ReportResultsTable";

export type ChatRole = "user" | "agent";

export interface AgentStep {
  tool: string;
  status: "completed" | "failed";
  description?: string;
}

export interface ChatMessageData {
  id: string;
  role: ChatRole;
  content: string;
  timestamp: Date;
  sqlQuery?: string;
  reportData?: Array<Record<string, unknown>>;
  reportColumns?: string[];
  error?: string;
  isLoading?: boolean;
  agentName?: string;
  canRetry?: boolean;
  onRetry?: () => void;
  steps?: AgentStep[];
  loadingSteps?: string[];  // For streaming step updates during loading
}

interface ChatMessageProps {
  message: ChatMessageData;
}

export const ChatMessage = ({ message }: ChatMessageProps): JSX.Element => {
  const [showSql, setShowSql] = useState<boolean>(false);

  const isUser = message.role === "user";
  const hasSql = !!message.sqlQuery;
  const isError = !!message.error;
  const loadingDotColor = isUser ? "bg-white/80" : "bg-neutral-400";
  const loadingLabelColor = isUser ? "text-white/80" : "text-neutral-500";

  return (
    <div className="w-full">
      <div className="mx-auto flex w-full max-w-3xl">
        <div
          className={cn(
            "flex w-full",
            isUser ? "justify-end" : "justify-start"
          )}
        >
          <div
            className={cn(
              "max-w-[85%] rounded-xl px-4 py-3 shadow-sm transition-colors",
              isUser ? "bg-[#0000FF] text-white" : "text-neutral-900",
              isError && "border border-red-400 bg-red-50 text-red-900"
            )}
          >
            {message.isLoading ? (
              <div className="space-y-2" aria-live="polite">
                {message.agentName && (
                  <p className={cn("text-xs font-medium", loadingLabelColor)}>
                    {message.agentName}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-2.5 w-2.5 rounded-full",
                      loadingDotColor,
                      "animate-[pulse_1.2s_ease-in-out_infinite]"
                    )}
                  />
                  <span
                    className={cn(
                      "h-2.5 w-2.5 rounded-full",
                      loadingDotColor,
                      "animate-[pulse_1.2s_ease-in-out_infinite]",
                      "[animation-delay:0.2s]"
                    )}
                  />
                  <span
                    className={cn(
                      "h-2.5 w-2.5 rounded-full",
                      loadingDotColor,
                      "animate-[pulse_1.2s_ease-in-out_infinite]",
                      "[animation-delay:0.4s]"
                    )}
                  />
                </div>
                {/* Show loading steps if available */}
                {message.loadingSteps && message.loadingSteps.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {message.loadingSteps.map((step, idx) => (
                      <p key={idx} className={cn("text-xs", loadingLabelColor)}>
                        ✓ {step}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div
                className={cn(
                  "prose prose-sm max-w-none break-words",
                  isUser ? "prose-invert" : "prose-neutral",
                  message.isLoading && "opacity-70"
                )}
                aria-live={message.isLoading ? "polite" : undefined}
              >
                <ReactMarkdown
                  components={{
                    // Override table rendering to hide markdown tables (we show ReportResultsTable instead)
                    table: () => null,
                    thead: () => null,
                    tbody: () => null,
                    tr: () => null,
                    th: () => null,
                    td: () => null,
                    // Style code blocks
                    code: ({ className, children }) => {
                      const isInline = !className;
                      return isInline ? (
                        <code className="bg-neutral-100 px-1 py-0.5 rounded text-sm">{children}</code>
                      ) : (
                        <code className={cn("block bg-neutral-900 text-neutral-100 p-2 rounded text-xs overflow-auto", className)}>
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            )}

            {isError && (
              <div className="mt-2 flex items-center gap-2">
                <p className="text-sm font-medium text-red-600">
                  {message.error}
                </p>
                {message.canRetry && message.onRetry && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                    onClick={message.onRetry}
                  >
                    Try Again
                  </Button>
                )}
              </div>
            )}

            {hasSql && !message.reportColumns?.length && !message.isLoading && (
              <div className="mt-3 space-y-2">
                <Button
                  variant={isUser ? "secondary" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8 text-xs",
                    isUser
                      ? "bg-white text-blue-700 hover:bg-neutral-100"
                      : "text-neutral-700"
                  )}
                  onClick={() => setShowSql((previous) => !previous)}
                >
                  {showSql ? "Hide SQL" : "Show SQL"}
                </Button>

                {showSql && (
                  <pre className="max-h-64 overflow-auto rounded-lg bg-neutral-900 p-3 text-xs text-neutral-100">
                    <code>{message.sqlQuery}</code>
                  </pre>
                )}
              </div>
            )}

            {/* Show completed steps */}
            {!message.isLoading && message.steps && message.steps.length > 0 && (
              <div className="mt-2 border-t border-neutral-200 pt-2">
                <p className="text-xs text-neutral-400 mb-1">Agent actions:</p>
                <div className="flex flex-wrap gap-1">
                  {message.steps.map((step, idx) => (
                    <span
                      key={idx}
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs",
                        step.status === "completed"
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      )}
                    >
                      {step.status === "completed" ? "✓" : "✗"} {step.description || step.tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!message.isLoading && (
              <div className="mt-2 text-right text-xs text-neutral-500">
                {message.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {message.reportColumns && message.reportColumns.length > 0 && message.reportData && (
        <div className="mt-4">
          <ReportResultsTable
            columns={message.reportColumns}
            data={message.reportData}
            sqlQuery={message.sqlQuery}
            isLoading={message.isLoading}
          />
        </div>
      )}
    </div>
  );
};
